import './HeadlineList.css';
import './PostView.css';
import { forwardRef, useRef, useEffect, useImperativeHandle, useContext } from 'react';
import { AppState } from '../context/general';
import { getSinglePost } from '../connector/post-engine';
import { formatPublishedTime, stripHtml } from '../utils/formatting';
import { newHtml, newSvg } from 't-lib-ui/helpers';
import type { UserPreferences } from '../schema-copy/api-schema';
import { createArrowRightSvg, createCheckmarkSvg, createLockSvg } from './svg-helpers';

// ── Constants ──────────────────────────────────────────────────────────

const ESTIMATED_HEIGHT = 80;
const SCROLL_DURATION = 300;
const BUFFER_ITEMS = 5;

// ── Types ──────────────────────────────────────────────────────────────

interface HeadlineEntry {
  feedId: string;
  postIndex: number;
  element: HTMLDivElement | null;
  height: number;
  loaded: boolean;
  title?: string;
  originalLink: string;
  summary: string;
  pubDate: string;
  isRead: boolean;
  // Read state when the view was built. "Keep Unread" only reverts posts read
  // during this session (auto-marks), never posts that were already read before.
  initiallyRead: boolean;
  isStarred: boolean;
  isReadLater: boolean;
  selected: boolean;
  manuallyUnread: boolean;
  displayFeedName?: string;
}

export interface HeadlineRendererProps {
  posts: [string, number][];
  startAt: number;
  onAllRead: () => void;
  onSelect: (feedId: string, postIndex: number) => void;
  onMark?: (feedId: string, postIndex: number, type: 'read', value: boolean) => void;
  showUnreadOnly?: boolean;
  onEndOfUnread?: () => void;
  onShowAll?: () => void;
  overflowMessage?: string;
  onOverflowAction?: () => void;
  onLoadMore?: () => void;
  loadMoreLabel?: string;
  loadMoreWithNextFeed?: boolean;
  userPreferences?: UserPreferences;
  nextUnreadFeed?: { id: string; name: string; isFolder?: boolean };
  onNavigateToFeed?: (feedId: string) => void;
  onKeepAllUnread?: () => void;
  isSavedList?: boolean;
  isUnreadList?: boolean;
}

export interface HeadlineRendererHandle {
  selectPost: (feedId: string, postIndex: number) => void;
  updatePostStatus: (feedId: string, postIndex: number, type: 'star' | 'readLater' | 'read', value: boolean) => void;
  markAllRead: () => void;
  markAllUnread: () => void;
  keepAllUnread: () => void;
  markOlderThanRead: (days: number) => void;
}

// ── SVG Helpers ────────────────────────────────────────────────────────

const STROKE_PROPS = { stroke: 'currentColor', 'stroke-width': '1.5', fill: 'none' } as const;

function createSpinnerSvg(): SVGElement {
  return newSvg('svg', { class: 'spinner spinning', width: '20', height: '20', viewBox: '0 0 24 24' },
    newSvg('path', { d: 'M10.14,1.16a11,11,0,0,0-9,8.92A1.59,1.59,0,0,0,2.46,12,1.52,1.52,0,0,0,4.11,10.7a8,8,0,0,1,6.66-6.61A1.42,1.42,0,0,0,12,2.69h0A1.57,1.57,0,0,0,10.14,1.16Z' }));
}

function createClockSvg(size: number): SVGElement {
  return newSvg('svg', { width: size, height: size, viewBox: '0 0 16 16', fill: 'none',
    style: { display: 'inline-block', verticalAlign: 'middle' } }, [
    newSvg('circle', { cx: '8', cy: '8', r: '7', ...STROKE_PROPS }),
    newSvg('path', { d: 'M8 4 L8 8 L11 10', ...STROKE_PROPS, 'stroke-linecap': 'round', 'stroke-linejoin': 'round' }),
  ]);
}

// ── DOM Creation ───────────────────────────────────────────────────────

function createLoadingPlaceholder(): HTMLDivElement {
  return newHtml('div', { class: 'headlineItemCard flowFocussable', tabindex: '0',
    style: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80px' } },
    createSpinnerSvg()) as HTMLDivElement;
}

function createHeadlineElement(entry: HeadlineEntry, feedName: string): HTMLDivElement {
  // Tweet-style posts have no title: the summary line becomes the primary text and
  // carries the star/read-later icons + link that would otherwise sit on the title.
  const titleChildren: (HTMLElement | SVGElement | string)[] = [];
  if (entry.isStarred)
    titleChildren.push(newHtml('span', { class: 'starIcon' }, '\u2605'));
  if (entry.isReadLater)
    titleChildren.push(newHtml('span', { class: 'readLaterIcon' }, createClockSvg(14)));
  titleChildren.push(entry.title ?? entry.summary);

  const children: (HTMLElement | SVGElement)[] = [
    newHtml('div', { class: 'headlineItemHeader' }, [
      newHtml('span', { class: 'headlineItemFeedName' }, feedName),
      newHtml('span', { class: 'headlineItemTime' }, entry.manuallyUnread
        ? [entry.pubDate ? formatPublishedTime(entry.pubDate) : '', createLockSvg(12)]
        : (entry.pubDate ? formatPublishedTime(entry.pubDate) : '')),
    ]),
    newHtml('a', { class: `headlineItemTitle${entry.title ? '' : ' noTitle'}`, href: entry.originalLink || '#' }, titleChildren),
  ];
  if (entry.title)
    children.push(newHtml('p', { class: 'headlineItemSummary' }, entry.summary));

  return newHtml('div', {
    class: `headlineItemCard flowFocussable ${entry.selected ? 'selected' : ''} ${entry.isRead ? 'read' : 'unread'}${entry.manuallyUnread ? ' locked' : ''}`,
    tabindex: '0', 'data-feed-id': entry.feedId, 'data-post-index': String(entry.postIndex),
  }, children) as HTMLDivElement;
}

// ── Easing ─────────────────────────────────────────────────────────────

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// ── Component ──────────────────────────────────────────────────────────

const HeadlineRenderer = forwardRef<HeadlineRendererHandle, HeadlineRendererProps>((props, ref) => {
  const { posts } = props;
  const appContext = useContext(AppState);
  const containerRef = useRef<HTMLDivElement>(null);

  const startAtRef = useRef(props.startAt);
  startAtRef.current = props.startAt;

  const onAllReadRef = useRef(props.onAllRead);
  onAllReadRef.current = props.onAllRead;
  const onSelectRef = useRef(props.onSelect);
  onSelectRef.current = props.onSelect;
  const showUnreadOnlyRef = useRef(props.showUnreadOnly);
  showUnreadOnlyRef.current = props.showUnreadOnly;
  const onEndOfUnreadRef = useRef(props.onEndOfUnread);
  onEndOfUnreadRef.current = props.onEndOfUnread;
  const onShowAllRef = useRef(props.onShowAll);
  onShowAllRef.current = props.onShowAll;
  const onOverflowActionRef = useRef(props.onOverflowAction);
  onOverflowActionRef.current = props.onOverflowAction;
  const onLoadMoreRef = useRef(props.onLoadMore);
  onLoadMoreRef.current = props.onLoadMore;
  const onKeepAllUnreadRef = useRef(props.onKeepAllUnread);
  onKeepAllUnreadRef.current = props.onKeepAllUnread;
  const onNavigateToFeedRef = useRef(props.onNavigateToFeed);
  onNavigateToFeedRef.current = props.onNavigateToFeed;
  const onMarkRef = useRef(props.onMark);
  onMarkRef.current = props.onMark;
  const userPreferencesRef = useRef(props.userPreferences);
  userPreferencesRef.current = props.userPreferences;
  const feedEngineRef = useRef(appContext.feedEngine);
  feedEngineRef.current = appContext.feedEngine;

  const entriesRef = useRef<HeadlineEntry[]>([]);
  // Posts used by the previous build, so an append-only growth ("Show more") can keep
  // the current scroll position instead of jumping back to startAt.
  const prevPostsRef = useRef<[string, number][]>([]);
  const renderedRangeRef = useRef<[number, number]>([-1, -1]);
  const selectedIndexRef = useRef(-1);

  const selectPostFn = useRef<HeadlineRendererHandle['selectPost']>(() => {});
  const updatePostStatusFn = useRef<HeadlineRendererHandle['updatePostStatus']>(() => {});
  const markAllReadFn = useRef<HeadlineRendererHandle['markAllRead']>(() => {});
  const markAllUnreadFn = useRef<HeadlineRendererHandle['markAllUnread']>(() => {});
  const keepAllUnreadFn = useRef<HeadlineRendererHandle['keepAllUnread']>(() => {});
  const markOlderThanReadFn = useRef<HeadlineRendererHandle['markOlderThanRead']>(() => {});

  useImperativeHandle(ref, () => ({
    selectPost: (...a) => selectPostFn.current(...a),
    updatePostStatus: (...a) => updatePostStatusFn.current(...a),
    markAllRead: () => markAllReadFn.current(),
    markAllUnread: () => markAllUnreadFn.current(),
    keepAllUnread: () => keepAllUnreadFn.current(),
    markOlderThanRead: (d) => markOlderThanReadFn.current(d),
  }), []);

  // Main lifecycle effect - rebuilds when posts change
  useEffect(() => {
    const container = containerRef.current!;
    if (!container) return;

    const startAt = startAtRef.current;

    // Detect an append-only growth (same posts plus more at the end) so "Show more"
    // keeps the scroll position. Capture scrollTop before clearing resets it to 0.
    const prevPosts = prevPostsRef.current;
    const isAppend = prevPosts.length > 0 && posts.length > prevPosts.length
      && prevPosts.every((p, i) => p[0] == posts[i][0] && p[1] == posts[i][1]);
    const preservedScrollTop = container.scrollTop;
    prevPostsRef.current = posts;

    // Clear previous content
    container.innerHTML = '';

    // Build entries with initial status from feedEngine
    const fe = feedEngineRef.current;
    const readCache = new Map<string, Set<number>>();
    const starCache = new Map<string, Set<number>>();
    const rlCache = new Map<string, Set<number>>();
    const getSet = (cache: Map<string, Set<number>>, feedId: string, getter: (id: string) => Set<number>) => {
      let s = cache.get(feedId);
      if (!s) { s = getter(feedId); cache.set(feedId, s); }
      return s;
    };

    const entries: HeadlineEntry[] = posts.map(([feedId, postIndex]) => {
      const isRead = getSet(readCache, feedId, id => fe.getReadForFeed(id)).has(postIndex);
      return {
        feedId, postIndex,
        element: null,
        height: ESTIMATED_HEIGHT,
        loaded: false,
        originalLink: '',
        summary: '',
        pubDate: '',
        isRead,
        initiallyRead: isRead,
        isStarred: getSet(starCache, feedId, id => fe.getStarredForFeed(id)).has(postIndex),
        isReadLater: getSet(rlCache, feedId, id => fe.getReadLaterForFeed(id)).has(postIndex),
        selected: false,
        manuallyUnread: false,
      };
    });
    entriesRef.current = entries;

    if (!entries.length) {
      renderedRangeRef.current = [-1, -1];
      return;
    }

    // Create upper spacer
    const spacer = document.createElement('div');
    spacer.style.height = `${ESTIMATED_HEIGHT * Math.max(0, startAt - 1)}px`;
    spacer.style.flexShrink = '0';
    container.appendChild(spacer);

    // Create bottom spacer
    const bottomSpacer = document.createElement('div');
    bottomSpacer.style.flexShrink = '0';
    let bottomSpacerHeight = 0;
    container.appendChild(bottomSpacer);

    // Create mark-all-read button (hidden in load-more mode, in saved lists, and for
    // regular feeds with auto-read on, where the end-of-list Next Feed row replaces it)
    const isRegularFeed = !props.isSavedList && !props.isUnreadList;
    const markAllReadBtn = newHtml('button', { class: 'markAllReadBottomButton flowFocussable' }, 'Mark all as read') as HTMLButtonElement;
    markAllReadBtn.addEventListener('click', () => onAllReadRef.current());
    const hideMarkAllRead = showUnreadOnlyRef.current || props.onLoadMore || props.isSavedList || isRegularFeed;
    const markAllReadEl = newHtml('div', { class: 'markAllReadBottom',
      style: { display: hideMarkAllRead ? 'none' : '' } }, markAllReadBtn);
    container.appendChild(markAllReadEl);

    // End-of-list button (shown when in unread-only mode and, with auto-read on, at the
    // end of any regular feed; hidden in load-more mode).
    // In auto-read mode, "Next Feed" goes to the next unread feed (with its name shown
    // below). If there is no next unread feed, it falls through to the Home page instead
    // \u2014 labelled "Home" with no subtitle.
    const nextFeed = props.nextUnreadFeed;
    const nextCol = newHtml('div', { class: 'endOfUnreadNextFeed' });
    // Next Feed / Home also marks the feed read, so show a checkmark while unread posts
    // remain and a right arrow once everything is read or locked (pure navigation).
    const navCheck = createCheckmarkSvg(14);
    const navArrow = createArrowRightSvg(14);
    const setNavAllRead = (allRead: boolean) => {
      navCheck.style.display = allRead ? 'none' : 'inline-block';
      navArrow.style.display = allRead ? 'inline-block' : 'none';
    };
    setNavAllRead(entries.length > 0 && entries.every(e => e.isRead || e.manuallyUnread));
    const nextBtn = newHtml('button', { class: 'markAllReadBottomButton flowFocussable sidewayFocus' }, [
      navCheck, navArrow,
      newHtml('span', { style: { marginLeft: '6px' } }, nextFeed ? (nextFeed.isFolder ? 'Next Folder' : 'Next Feed') : 'Home'),
    ]);
    if (nextFeed) {
      nextBtn.addEventListener('click', () => {
        onEndOfUnreadRef.current?.();
        onNavigateToFeedRef.current?.(nextFeed.id);
      });
      nextCol.appendChild(nextBtn);
      nextCol.appendChild(newHtml('span', { class: 'endOfUnreadNextFeedName' }, nextFeed.name));
    } else {
      nextBtn.addEventListener('click', () => {
        onEndOfUnreadRef.current?.();
        onNavigateToFeedRef.current?.('home');
      });
      nextCol.appendChild(nextBtn);
    }
    const endOfUnreadContent: HTMLElement = nextCol;
    const endOfUnreadEl = newHtml('div', { class: 'markAllReadBottom',
      style: { display: ((showUnreadOnlyRef.current || isRegularFeed) && !props.onLoadMore) ? '' : 'none' } }, endOfUnreadContent);
    container.appendChild(endOfUnreadEl);

    // "Load More" button (replaces mark-all-read + overflow when onLoadMore is set).
    // Browse-only "Show more" uses the white (secondary) style and stands alone — the
    // narrow headline column has no room for a Next Feed / Home button beside it.
    if (props.onLoadMore) {
      const secondary = props.loadMoreWithNextFeed;
      const loadMoreBtn = newHtml('button',
        { class: `markAllReadBottomButton flowFocussable${secondary ? ' keepAllUnreadBtn' : ''}` },
        props.loadMoreLabel ?? 'Load more');
      loadMoreBtn.addEventListener('click', () => onLoadMoreRef.current?.());
      container.appendChild(newHtml('div', { class: 'markAllReadBottom' }, loadMoreBtn));
    }

    // Overflow message (shown when more posts exist beyond the displayed limit)
    if (props.overflowMessage && !props.onLoadMore) {
      const overflowBtn = newHtml('button', { class: 'markAllReadBottomButton flowFocussable' }, 'Mark all as read and load more');
      overflowBtn.addEventListener('click', () => onOverflowActionRef.current?.());
      container.appendChild(newHtml('div', { class: 'markAllReadBottom' }, [
        newHtml('p', { class: 'endOfUnreadText' }, props.overflowMessage),
        overflowBtn,
      ]));
    }

    // Scroll mode declared early so ResizeObserver callback can reference it
    let scrollMode: 'idle' | 'scrolling' | 'watching' = 'idle';

    // ResizeObserver to track headline height changes
    const resizeMap = new Map<Element, number>();
    const resizeObserver = new ResizeObserver(observed => {
      for (const oe of observed) {
        const idx = resizeMap.get(oe.target);
        if (idx == null) continue;
        const el = oe.target as HTMLElement;
        const rect = el.getBoundingClientRect();
        const oldH = entries[idx].height;
        if (rect.height == oldH) continue;
        entries[idx].height = rect.height;
        if (scrollMode != 'scrolling' && rect.bottom < container.getBoundingClientRect().top)
          container.scrollTop += rect.height - oldH;
      }
    });

    // Reaching the bottom of the headline list does NOT mark the remaining posts read:
    // users found it confusing when the rest of the list flipped to read at once. Only
    // scroll-past auto-marking (below), PostView, and the Next Feed button mark posts read.
    let userHasScrolled = false; // Driven by real user input; scroll events alone aren't trusted
    // because programmatic scrollTop assignments during feed loading would falsely trip it.
    const markUserInteracted = () => { userHasScrolled = true; };
    container.addEventListener('wheel', markUserInteracted, { passive: true });
    container.addEventListener('touchstart', markUserInteracted, { passive: true });
    container.addEventListener('pointerdown', markUserInteracted, { passive: true });
    container.addEventListener('keydown', markUserInteracted);

    // ── Local Helpers ────────────────────────────────────────────────

    function getFeedInfo(feedId: string) {
      return feedEngineRef.current.getFeed(feedId);
    }

    function insertItem(i: number, before?: HTMLElement | null) {
      const entry = entries[i];
      if (entry.element) return;

      let el: HTMLDivElement;
      if (entry.loaded) {
        const fi = getFeedInfo(entry.feedId);
        el = createHeadlineElement(entry, entry.displayFeedName ?? (fi ? (fi.shortTitle || fi.title) : ''));
      } else {
        el = createLoadingPlaceholder();
        el.setAttribute('data-feed-id', entry.feedId);
        el.setAttribute('data-post-index', String(entry.postIndex));
      }
      entry.element = el;
      container.insertBefore(el, before || bottomSpacer);
      resizeObserver.observe(el);
      resizeMap.set(el, i);

      if (!entry.loaded) fetchHeadlineData(i);
    }

    async function fetchHeadlineData(i: number) {
      const entry = entries[i];
      if (entry.loaded) return;

      // Unsubscribed feeds (possible in saved lists) aren't in the engine's feed list: resolve them
      // like PostRenderer does, or the row would spin forever.
      const fi = getFeedInfo(entry.feedId) ?? await feedEngineRef.current.ensureFeedInfo(entry.feedId).catch(() => undefined);
      if (!fi) {
        showHeadlineError(entry);
        return;
      }

      try {
        const post = await getSinglePost(
          { id: fi.id, postCount: fi.postCount, passphrase: fi.passphrase, cdn: fi.cdn, syncInstance: fi.syncInstance },
          entry.postIndex
        );
        entry.title = post.title;
        entry.originalLink = post.link || '';
        entry.summary = stripHtml(post.description);
        entry.pubDate = post.pubDate || '';
        entry.displayFeedName = (post as { feedName?: string }).feedName ?? fi.shortTitle ?? fi.title;
        entry.loaded = true;

        if (!entry.element?.isConnected) return;

        const oldH = entry.element.getBoundingClientRect().height;
        const wasAbove = entry.element.getBoundingClientRect().bottom < container.getBoundingClientRect().top;

        const feedName = entry.displayFeedName;
        const newEl = createHeadlineElement(entry, feedName);
        resizeObserver.unobserve(entry.element);
        resizeMap.delete(entry.element);
        entry.element.replaceWith(newEl);
        entry.element = newEl;
        resizeObserver.observe(newEl);
        resizeMap.set(newEl, i);

        const newH = newEl.getBoundingClientRect().height;
        entry.height = newH;
        if (wasAbove) container.scrollTop += newH - oldH;
      } catch (e) {
        console.error(`Failed to fetch headline ${entry.feedId}/${entry.postIndex}:`, e);
        showHeadlineError(entry, fi.shortTitle || fi.title);
      }
    }

    function showHeadlineError(entry: HeadlineEntry, feedName?: string) {
      if (!entry.element?.isConnected) return;
      entry.element.innerHTML = '';
      entry.element.className = 'headlineItemCard';
      entry.element.style.cssText = 'display:flex;justify-content:center;align-items:center;height:80px;color:var(--gray-550);font-size:14px';
      entry.element.setAttribute('data-feed-id', entry.feedId);
      entry.element.setAttribute('data-post-index', String(entry.postIndex));
      entry.element.textContent = feedName ? `Failed to load post from ${feedName}` : 'Failed to load post (unknown feed)';
    }

    function updateMarkAllRead() {
      const allRead = entries.every(e => e.isRead);
      const allLocked = entries.length > 0 && entries.every(e => e.manuallyUnread);
      // When everything is locked, "Mark all as read" makes no sense — hide the button.
      markAllReadEl.style.display = allLocked || hideMarkAllRead ? 'none' : '';
      markAllReadBtn.disabled = allRead;
      markAllReadBtn.textContent = allRead ? 'All items read' : 'Mark all as read';
      setNavAllRead(entries.length > 0 && entries.every(e => e.isRead || e.manuallyUnread));
    }

    // ── Virtualization ──────────────────────────────────────────────

    function handleScrollUpdate() {
      let [first, last] = renderedRangeRef.current;
      if (first < 0) return;

      const cr = container.getBoundingClientRect();
      const bufferPx = BUFFER_ITEMS * ESTIMATED_HEIGHT;

      // Extend below
      while (last < entries.length - 1) {
        const el = entries[last].element;
        if (el && el.getBoundingClientRect().bottom > cr.bottom + bufferPx) break;
        last++;
        bottomSpacerHeight = Math.max(0, bottomSpacerHeight - entries[last].height);
        bottomSpacer.style.height = `${bottomSpacerHeight}px`;
        insertItem(last);
      }

      // Extend above
      if (first > 0) {
        const spacerRect = spacer.getBoundingClientRect();
        if (spacerRect.bottom > cr.top - bufferPx) {
          const newFirst = Math.max(0, first - BUFFER_ITEMS);
          if (newFirst < first) {
            const anchorEl = entries[first].element!;
            const anchorTop = anchorEl.getBoundingClientRect().top;

            for (let j = first - 1; j >= newFirst; j--) {
              const h = entries[j].height;
              const sh = Math.max(0, parseFloat(spacer.style.height) - h);
              spacer.style.height = `${sh}px`;
              insertItem(j, entries[j + 1]?.element);
            }

            container.scrollTop += anchorEl.getBoundingClientRect().top - anchorTop;
            first = newFirst;
          }
        }
      }

      // Skip trimming during scroll animation/watching — the target element must stay in the DOM
      if (scrollMode == 'idle') {
        // Trim from top
        const trimPx = (BUFFER_ITEMS + 5) * ESTIMATED_HEIGHT;
        while (first < last) {
          const el = entries[first].element;
          if (!el || el.getBoundingClientRect().bottom >= cr.top - trimPx) break;

          const h = el.getBoundingClientRect().height;
          entries[first].height = h;
          resizeObserver.unobserve(el);
          resizeMap.delete(el);
          el.remove();
          entries[first].element = null;
          spacer.style.height = `${(parseFloat(spacer.style.height) || 0) + h}px`;
          first++;
        }

        // Trim from bottom
        while (last > first) {
          const el = entries[last].element;
          if (!el || el.getBoundingClientRect().top <= cr.bottom + trimPx) break;

          const h = el.getBoundingClientRect().height;
          entries[last].height = h;
          resizeObserver.unobserve(el);
          resizeMap.delete(el);
          el.remove();
          entries[last].element = null;
          bottomSpacerHeight += h;
          bottomSpacer.style.height = `${bottomSpacerHeight}px`;
          last--;
        }
      }

      renderedRangeRef.current = [first, last];
    }

    // ── Auto-mark-read on scroll ────────────────────────────────────

    function updateSeenAndMarkRead() {
      if (!userHasScrolled) return;

      const cr = container.getBoundingClientRect();
      const threshold = cr.top + cr.height * (1 / 5);
      const [first, last] = renderedRangeRef.current;
      let changed = false;

      // Find the last rendered entry whose bottom is within the upper 2/3 of the viewport.
      // Entries before the rendered range have scrolled above the viewport and are also read.
      let lastToMarkIdx = first - 1;
      for (let i = first; i <= last; i++) {
        const entry = entries[i];
        if (!entry.element) continue;
        if (entry.element.getBoundingClientRect().bottom <= threshold)
          lastToMarkIdx = i;
      }

      for (let i = 0; i <= lastToMarkIdx; i++) {
        const entry = entries[i];
        if (entry.isRead || entry.manuallyUnread) continue;
        entry.isRead = true;
        onMarkRef.current?.(entry.feedId, entry.postIndex, 'read', true);
        rebuildEntry(i);
        changed = true;
      }

      if (changed) updateMarkAllRead();
    }

    // ── Scroll State Machine ─────────────────────────────────────────

    const WATCH_DURATION = 5000;
    let scrollTarget: { kind: 'absolute'; position: number } | { kind: 'headline'; idx: number } | null = null;
    let scrollStartTop = 0;
    let scrollStartTime = 0;
    let watchStartTime = 0;
    let watchExpectedScrollTop = 0;
    let watchLastTargetTop = 0;
    let scrollLoopId = 0;

    function getScrollTargetPos(): number | null {
      if (!scrollTarget) return null;
      if (scrollTarget.kind == 'absolute') return scrollTarget.position;
      const entry = entries[scrollTarget.idx];
      if (!entry?.element) return null;
      const r = entry.element.getBoundingClientRect();
      const cr = container.getBoundingClientRect();
      return container.scrollTop + r.top - cr.top;
    }

    function beginScrolling(newTarget: NonNullable<typeof scrollTarget>) {
      cancelAnimationFrame(rafId);
      scrollTarget = newTarget;
      scrollStartTop = container.scrollTop;
      scrollStartTime = 0;
      scrollMode = 'scrolling';
      container.classList.add('scrollAnimating');
      scheduleScrollLoop();
    }

    function goIdle() {
      scrollMode = 'idle';
      scrollTarget = null;
      container.classList.remove('scrollAnimating');
    }

    function scheduleScrollLoop() {
      cancelAnimationFrame(scrollLoopId);
      scrollLoopId = requestAnimationFrame(scrollLoop);
    }

    function scrollLoop(ts: number) {
      if (scrollMode == 'scrolling') {
        if (!scrollStartTime) scrollStartTime = ts;
        handleScrollUpdate();

        const targetPos = getScrollTargetPos();
        if (targetPos == null) {
          scheduleScrollLoop();
          return;
        }

        const t = Math.min((ts - scrollStartTime) / SCROLL_DURATION, 1);
        container.scrollTop = scrollStartTop + (targetPos - scrollStartTop) * easeInOutCubic(t);

        if (t >= 1) {
          if (scrollTarget?.kind == 'headline') {
            const entry = entries[scrollTarget.idx];
            scrollMode = 'watching';
            watchStartTime = ts;
            watchExpectedScrollTop = container.scrollTop;
            watchLastTargetTop = entry?.element ? entry.element.getBoundingClientRect().top : 0;
            container.classList.remove('scrollAnimating');
          } else {
            goIdle();
            handleScrollUpdate();
            return;
          }
        }
        scheduleScrollLoop();
        return;
      }

      if (scrollMode == 'watching') {
        if (ts - watchStartTime > WATCH_DURATION) {
          goIdle();
          return;
        }

        if (Math.abs(container.scrollTop - watchExpectedScrollTop) > 1) {
          goIdle();
          return;
        }

        if (scrollTarget?.kind == 'headline') {
          const entry = entries[scrollTarget.idx];
          if (entry?.element) {
            const currentTop = entry.element.getBoundingClientRect().top;
            const delta = currentTop - watchLastTargetTop;
            if (Math.abs(delta) > 0.5) {
              container.scrollTop += delta;
              watchExpectedScrollTop = container.scrollTop;
              watchLastTargetTop = entry.element.getBoundingClientRect().top;
            }
          }
        }
        scheduleScrollLoop();
      }
    }

    // ── Selection Helpers ────────────────────────────────────────────

    function deselectCurrent() {
      const prevIdx = selectedIndexRef.current;
      if (prevIdx >= 0 && prevIdx < entries.length) {
        entries[prevIdx].selected = false;
        entries[prevIdx].element?.classList.remove('selected');
      }
    }

    function selectEntry(idx: number) {
      entries[idx].selected = true;
      selectedIndexRef.current = idx;
      entries[idx].element?.classList.add('selected');
    }

    function isEntryFullyVisible(idx: number) {
      const entryEl = entries[idx]?.element;
      if (!entryEl) return false;

      const containerRect = container.getBoundingClientRect();
      const entryRect = entryEl.getBoundingClientRect();
      const tolerance = 1;
      return entryRect.top >= containerRect.top - tolerance && entryRect.bottom <= containerRect.bottom + tolerance;
    }

    function scrollToEntry(idx: number) {
      const [first, last] = renderedRangeRef.current;

      // Outside rendered range - teleport
      if (idx < first || idx > last) {
        let sh = 0;
        for (let i = 0; i < Math.max(0, idx - 1); i++)
          sh += entries[i].height;

        spacer.style.height = `${sh}px`;

        // Remove all rendered elements
        for (let i = first; i <= last; i++) {
          if (entries[i].element) {
            resizeObserver.unobserve(entries[i].element!);
            resizeMap.delete(entries[i].element!);
            entries[i].element!.remove();
            entries[i].element = null;
          }
        }

        const newFirst = Math.max(0, idx - 1);
        const newLast = Math.min(entries.length - 1, idx + BUFFER_ITEMS);
        renderedRangeRef.current = [newFirst, newLast];

        for (let i = newFirst; i <= newLast; i++)
          insertItem(i);

        // Recalculate bottom spacer
        bottomSpacerHeight = 0;
        for (let i = newLast + 1; i < entries.length; i++)
          bottomSpacerHeight += entries[i].height;
        bottomSpacer.style.height = `${bottomSpacerHeight}px`;

        container.scrollTop = sh;
      }

      if (!entries[idx].element) return;
      beginScrolling({ kind: 'headline', idx });
    }

    // ── Event Listeners ─────────────────────────────────────────────

    let rafId = 0;
    const onScroll = () => {
      if (scrollMode == 'scrolling') return;
      if (scrollMode == 'watching') {
        if (Math.abs(container.scrollTop - watchExpectedScrollTop) > 1) {
          scrollMode = 'idle';
          cancelAnimationFrame(scrollLoopId);
        } else return;
      }
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        handleScrollUpdate();
        updateSeenAndMarkRead();
      });
    };
    container.addEventListener('scroll', onScroll, { passive: true });

    const onClick = (e: MouseEvent) => {
      const headlineLink = (e.target as HTMLElement).closest('a.headlineItemTitle');
      if (headlineLink && e.button == 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey)
        e.preventDefault();

      const card = (e.target as HTMLElement).closest('[data-feed-id]') as HTMLElement | null;
      if (!card) return;

      const feedId = card.dataset.feedId!;
      const postIndex = Number(card.dataset.postIndex);

      const idx = entries.findIndex(e => e.feedId == feedId && e.postIndex == postIndex);
      if (idx < 0) return;

      deselectCurrent();
      selectEntry(idx);
      onSelectRef.current(feedId, postIndex);
    };
    container.addEventListener('click', onClick);

    // ── Initial Render ──────────────────────────────────────────────

    const clampedStart = Math.max(0, Math.min(startAt, entries.length - 1));
    const firstIdx = Math.max(0, clampedStart - 1);
    const lastIdx = Math.min(entries.length - 1, clampedStart + 1);
    renderedRangeRef.current = [firstIdx, lastIdx];

    for (let i = firstIdx; i <= lastIdx; i++)
      insertItem(i);

    // Fill viewport below
    {
      let last = lastIdx;
      const cr = container.getBoundingClientRect();
      while (last < entries.length - 1) {
        const el = entries[last].element;
        if (el && el.getBoundingClientRect().bottom > cr.bottom + ESTIMATED_HEIGHT) break;
        last++;
        insertItem(last);
      }
      renderedRangeRef.current[1] = last;

      // Initialize bottom spacer
      bottomSpacerHeight = 0;
      for (let i = last + 1; i < entries.length; i++)
        bottomSpacerHeight += entries[i].height;
      bottomSpacer.style.height = `${bottomSpacerHeight}px`;
    }

    // Set initial scroll position — after the content below is laid out and the bottom
    // spacer covers the remaining items, otherwise a tall top spacer with only a few
    // rendered items leaves scrollHeight too small and scrollTop gets clamped short of
    // the target. That clamp left an empty gap at the top with the first items missing
    // after returning to a long-idle feed (a background poll prepends posts, pushing
    // startAt deep, so the top spacer is tall). For an append-only growth the prefix
    // layout is unchanged, so restore the prior scroll position (e.g. "Show more").
    container.scrollTop = isAppend ? preservedScrollTop : (parseFloat(spacer.style.height) || 0);

    // The fill loop above measured at scrollTop 0, against the top of the scroll area,
    // but we just jumped to the end of the top spacer. When that spacer is taller than a
    // viewport (startAt deep in the list), the filled band ends up entirely above the
    // final viewport, leaving blank space that only heals on the first scroll. Re-run the
    // virtualization against the position we actually landed on.
    handleScrollUpdate();

    // Mark startAt as selected
    if (clampedStart >= 0 && clampedStart < entries.length) {
      selectEntry(clampedStart);
    }

    updateMarkAllRead();

    // ── Imperative Handle ───────────────────────────────────────────

    selectPostFn.current = (feedId, postIndex) => {
      const idx = entries.findIndex(e => e.feedId == feedId && e.postIndex == postIndex);
      if (idx < 0) return;

      deselectCurrent();
      selectEntry(idx);
      if (isEntryFullyVisible(idx)) return;
      scrollToEntry(Math.max(0, idx - 1));
    };

    updatePostStatusFn.current = (feedId, postIndex, type, value) => {
      const idx = entries.findIndex(e => e.feedId == feedId && e.postIndex == postIndex);
      if (idx < 0) return;
      const entry = entries[idx];

      if (type == 'read') {
        entry.isRead = value;
        if (value) entry.manuallyUnread = false;
      }
      else if (type == 'star') entry.isStarred = value;
      else if (type == 'readLater') entry.isReadLater = value;

      if (type == 'read') updateMarkAllRead();

      if (!entry.loaded || !entry.element?.isConnected) return;

      const fi = getFeedInfo(entry.feedId);
      const oldEl = entry.element;
      const newEl = createHeadlineElement(entry, fi ? (fi.shortTitle || fi.title) : '');
      resizeObserver.unobserve(oldEl);
      resizeMap.delete(oldEl);
      oldEl.replaceWith(newEl);
      entry.element = newEl;
      resizeObserver.observe(newEl);
      resizeMap.set(newEl, idx);
    };

    // ── Batch Mark Operations ────────────────────────────────────────

    function rebuildEntry(idx: number) {
      const entry = entries[idx];
      if (!entry.loaded || !entry.element?.isConnected) return;
      const fi = getFeedInfo(entry.feedId);
      const oldEl = entry.element;
      const newEl = createHeadlineElement(entry, fi ? (fi.shortTitle || fi.title) : '');
      resizeObserver.unobserve(oldEl);
      resizeMap.delete(oldEl);
      oldEl.replaceWith(newEl);
      entry.element = newEl;
      resizeObserver.observe(newEl);
      resizeMap.set(newEl, idx);
    }

    markAllReadFn.current = () => {
      let changed = false;
      for (let i = 0; i < entries.length; i++) {
        if (entries[i].isRead) continue;
        entries[i].isRead = true;
        rebuildEntry(i);
        changed = true;
      }
      if (changed) updateMarkAllRead();
    };

    markAllUnreadFn.current = () => {
      let changed = false;
      for (let i = 0; i < entries.length; i++) {
        if (!entries[i].isRead) continue;
        entries[i].isRead = false;
        rebuildEntry(i);
        changed = true;
      }
      if (changed) updateMarkAllRead();
    };

    keepAllUnreadFn.current = () => {
      let changed = false;
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        if (entry.manuallyUnread && !entry.isRead) continue;
        const wasRead = entry.isRead;
        entry.manuallyUnread = true;
        entry.isRead = false;
        if (wasRead) onMarkRef.current?.(entry.feedId, entry.postIndex, 'read', false);
        rebuildEntry(i);
        changed = true;
      }
      if (changed) updateMarkAllRead();
    };

    markOlderThanReadFn.current = (days) => {
      const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
      let changed = false;
      for (let i = 0; i < entries.length; i++) {
        const entry = entries[i];
        if (entry.isRead || !entry.loaded || !entry.pubDate) continue;
        if (new Date(entry.pubDate).getTime() < cutoff) {
          entry.isRead = true;
          rebuildEntry(i);
          changed = true;
        }
      }
      if (changed) updateMarkAllRead();
    };

    // ── Cleanup ─────────────────────────────────────────────────────

    return () => {
      container.removeEventListener('scroll', onScroll);
      container.removeEventListener('click', onClick);
      container.removeEventListener('wheel', markUserInteracted);
      container.removeEventListener('touchstart', markUserInteracted);
      container.removeEventListener('pointerdown', markUserInteracted);
      container.removeEventListener('keydown', markUserInteracted);
      cancelAnimationFrame(rafId);
      cancelAnimationFrame(scrollLoopId);
      scrollMode = 'idle';
      resizeObserver.disconnect();
    };
  }, [posts]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div className="headlineItems" ref={containerRef} />;
});

export default HeadlineRenderer;
